import { downloadArtifactAsDocX } from '@/artifacts/fetch/get-download-artifact-docx'
import { ViewerDocumentKind, ViewerDownloadTargetKind, type ContentViewerSession, type ViewerDownloadTarget } from '../store/schemas'
import { store } from '@/store/store'
import { GlobalToastType, kSanitizationAllowedTags, kToastDurationShortMs, kToastDurationMediumMs } from '@/constants/constants-ui'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { downloadFile } from '@/routes/dashboard/files/utils/files-utils'
import { saveHTMLasDocx } from '@/chat-common/fetch/post-download-html-docx'
import sanitizeHtml from 'sanitize-html'
import { formatHtmlForDocXTransformation } from '@/chat-common/util/download-conversation'
import { AgentReferenceType } from '@/agent/references/store/schemas'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { ArtifactExportType } from '@/analytics/enums/artifact-template.enums'
import { selectArtifactById } from '@/artifacts/store/selectors'
import { getAgentReferenceHtml } from '../fetch/get-agent-reference'
import { downloadFile as downloadUserFilesPipelineFile } from '@/user_files/files/fetch/download-user-file'
import { resolveUserFile } from '@/user_files/files/utils/resolve-user-files'
import { fetchWritingSampleReferenceContent } from '@/agent/references/fetch/fetch-writing-sample-reference-content'
import { saveAs } from 'file-saver'

/**
 * Handles downloading the active content viewer session.
 * @param contentViewerContent
 * @returns
 */
export default async function handleContentViewerDownload(
  contentViewerContent: ContentViewerSession | null,
  trackEvent?: (event: AnalyticsEvent, properties?: Record<string, string | number | boolean>) => void
) {
  // Catch-all error toast for any download path that throws. Branches that need more than a generic
  // toast — e.g. the artifact branch, which also logs failure analytics — handle their own errors.
  try {
    await downloadByContentType(contentViewerContent, trackEvent)
  } catch (error) {
    console.error('Content viewer download failed', error)
    store.dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'File download failed.', durationMs: kToastDurationMediumMs }))
  }
}

async function downloadByContentType(
  contentViewerContent: ContentViewerSession | null,
  trackEvent?: (event: AnalyticsEvent, properties?: Record<string, string | number | boolean>) => void
) {
  if (!contentViewerContent) return

  await downloadBySession(contentViewerContent, trackEvent)
}

async function downloadBySession(
  session: ContentViewerSession,
  trackEvent?: (event: AnalyticsEvent, properties?: Record<string, string | number | boolean>) => void
) {
  const downloadTarget = session.document.downloadTarget
  if (!downloadTarget) return

  switch (downloadTarget.kind) {
    case ViewerDownloadTargetKind.ARTIFACT_DOCX: {
      const artifactVersionId = downloadTarget.artifactVersionId
      try {
        const downloadedFileName = await downloadArtifactAsDocX(artifactVersionId)
        store.dispatch(
          openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Download complete: ${downloadedFileName}`, durationMs: kToastDurationShortMs })
        )
        trackEvent?.(AnalyticsEvent.ArtifactDownloadSuccess, {
          artifactId: artifactVersionId,
          artifactVersionId,
          isMedChronArtifact: false,
        })
        // The owning artifact id is the stable draft-level join key (the version id drifts across edits).
        const draftArtifactId = selectArtifactById(store.getState(), { artifactVersionId })?.artifact_id
        // Always a plain download here; templated exports fire this event from useArtifactExport
        trackEvent?.(AnalyticsEvent.ArtifactExportCompleted, {
          artifactVersionId,
          ...(draftArtifactId ? { artifactId: draftArtifactId } : {}),
          artifactExportType: ArtifactExportType.Download,
          templateApplied: false,
        })
      } catch (error) {
        store.dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'File download failed.', durationMs: kToastDurationMediumMs }))
        trackEvent?.(AnalyticsEvent.ArtifactDownloadFailed, {
          artifactId: artifactVersionId,
          artifactVersionId,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
          isMedChronArtifact: false,
        })
      }
      break
    }

    case ViewerDownloadTargetKind.USER_FILE_ORIGINAL: {
      await downloadUserFile(downloadTarget.userFileId)
      break
    }

    case ViewerDownloadTargetKind.LEGACY_OWNER_FILE: {
      const downloadedFileName = await downloadOwnerFile(downloadTarget.filePath)
      store.dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Download complete: ${downloadedFileName}`, durationMs: kToastDurationShortMs }))
      break
    }

    case ViewerDownloadTargetKind.RICH_REFERENCE_DOCX: {
      const downloadedFileName = await downloadRichReferenceTargetAsDocX(downloadTarget)
      if (downloadedFileName !== undefined) {
        store.dispatch(
          openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Download complete: ${downloadedFileName}`, durationMs: kToastDurationShortMs })
        )
      }
      break
    }

    case ViewerDownloadTargetKind.WRITING_SAMPLE_PDF: {
      const content = await fetchWritingSampleReferenceContent(downloadTarget.referenceId, downloadTarget.matterId)
      const fileName = content.fileName || downloadTarget.fileName
      saveAs(content.blob, fileName)
      store.dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Download complete: ${fileName}`, durationMs: kToastDurationShortMs }))
      break
    }
  }
}

// Resolve a user file by id and download its original upload. Shared by the citation and
// USER_FILE download paths, which both have an id but not the original path.
async function downloadUserFile(userFileId: string) {
  const userFile = await resolveUserFile(userFileId)
  if (!userFile) throw new Error(`Could not resolve user file ${userFileId}`)

  await downloadUserFilesPipelineFile({ fileId: userFile.id, storagePath: userFile.storage_path, fileName: userFile.original_filename })
  store.dispatch(
    openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Download complete: ${userFile.original_filename}`, durationMs: kToastDurationShortMs })
  )
}

async function downloadOwnerFile(filePath: string) {
  if (!filePath) {
    throw new Error('No file path available for download.')
  }
  const fileName = await downloadFile(filePath)
  return fileName
}

async function downloadRichReferenceTargetAsDocX(target: Extract<ViewerDownloadTarget, { kind: ViewerDownloadTargetKind.RICH_REFERENCE_DOCX }>) {
  const referenceType = target.sourceKind === ViewerDocumentKind.CASELAW ? AgentReferenceType.CASELAW : AgentReferenceType.LRR
  const htmlContent = await getAgentReferenceHtml(referenceType, target.storagePath).then((res) => res?.data)
  if (!htmlContent) {
    console.error('Error fetching reference HTML')
    return
  }

  const sanitizedHtml = sanitizeHtml(htmlContent, {
    allowedTags: kSanitizationAllowedTags,
    transformTags: { pre: 'p', code: 'p' },
  })
  const outputHtml = formatHtmlForDocXTransformation(sanitizedHtml)
  return saveHTMLasDocx({ html: outputHtml, fileName: target.fileName })
}
